import React from 'react';

export const DEFAULT_CENTER = [ 19.1972, 72.9722];
export const DATA_PAGE_SIZE = 25;

/*
 * Workspace tabs and their URLs.
 *
 * Each tab is a real address, so a screen can be linked to, bookmarked, opened in
 * a second browser tab, and reached with the browser's back and forward buttons.
 * The URL is the source of truth for which tab is showing — the tab is derived
 * from the path rather than tracked separately, so the two cannot disagree.
 *
 * `data` is the internal name of the dashboard, kept because components already
 * use it; its address is /home.
 */
export const TAB_PATHS = {
  data: '/home',
  stops: '/stops',
  map: '/map',
  routes: '/routes'
};

export const PATH_TABS = Object.fromEntries(
  Object.entries(TAB_PATHS).map(([tab, path]) => [path, tab])
);

export const DEFAULT_TAB = 'data';
export const DEFAULT_TAB_PATH = TAB_PATHS[DEFAULT_TAB];

// Which tab a pathname denotes, or null when it is not a workspace address.
export function tabForPath(pathname) {
  const normalized = String(pathname || '').replace(/\/+$/, '').toLowerCase() || '/';
  return PATH_TABS[normalized] || null;
}

export function pathForTab(tab) {
  return TAB_PATHS[tab] || DEFAULT_TAB_PATH;
}

// Distinct line colors used when several routes are shown on the map at once.
export const ROUTE_PALETTE = [
  '#4f46e5',
  '#059669',
  '#d97706',
  '#dc2626',
  '#0891b2',
  '#7c3aed',
  '#db2777',
  '#65a30d',
  '#2563eb',
  '#ea580c',
  '#0d9488',
  '#c026d3'
];

export function routePaletteColor(index) {
  return ROUTE_PALETTE[((index % ROUTE_PALETTE.length) + ROUTE_PALETTE.length) % ROUTE_PALETTE.length];
}

export function highlightMatch(text, query) {
  const safeText = String(text ?? '');

  if (!query || !query.trim()) {
    return safeText;
  }

  const matchIndex = safeText.toLowerCase().indexOf(query.toLowerCase());

  if (matchIndex === -1) {
    return safeText;
  }

  return (
    <span>
      {safeText.slice(0, matchIndex)}
      <mark className="bg-amber-100 text-amber-900 rounded-sm px-0.5">
        {safeText.slice(matchIndex, matchIndex + query.length)}
      </mark>
      {safeText.slice(matchIndex + query.length)}
    </span>
  );
}

export function round6(value) {
  return parseFloat(Number(value).toFixed(6));
}

// Client-side mirror of the backend RouteStopCode logic:
// StopID(4) + RouteNo(3) + Direction(2: Up=01/Down=00) + Sequence(2).
function stopId4(humanStopCode) {
  const digits = (String(humanStopCode || '').match(/\d+/g) || []).join('');
  if (!digits) return '0000';
  return digits.length >= 4 ? digits.slice(-4) : digits.padStart(4, '0');
}

function routeNo3(routeNumber) {
  const match = String(routeNumber || '').match(/\d+/);
  const num = match ? match[0] : '';
  if (!num) return '000';
  return num.length >= 3 ? num.slice(-3) : num.padStart(3, '0');
}

function direction2(direction) {
  const text = String(direction || '').trim().toLowerCase();
  if (text === 'up' || text === '1') return '01';
  if (text === 'down' || text === '0') return '00';
  return '00';
}

export function buildRouteStopCode({ stopCode, routeNumber, direction, sequence }) {
  const n = parseInt(sequence, 10);
  const seq2 = Number.isFinite(n) ? String(n).padStart(2, '0') : '00';
  return `${stopId4(stopCode)}${routeNo3(routeNumber)}${direction2(direction)}${seq2}`;
}

// Great-circle (straight-line) distance between two {lat,lng} points, in metres.
export function haversineMeters(a, b) {
  if (!a || !b) return 0;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function formatDistance(meters) {
  if (!Number.isFinite(meters)) return '—';
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(meters < 10000 ? 2 : 1)} km`;
}

export function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

// Per-segment straight-line distances + total for an ordered list of stops.
export function routeSegmentDistances(points = []) {
  const segments = [];
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    const meters = haversineMeters(a, b);
    total += meters;
    segments.push({
      meters,
      mid: [(a.lat + b.lat) / 2, (a.lng + b.lng) / 2]
    });
  }
  return { segments, total };
}

export function normalizeImportHeader(header) {
  return String(header).trim().toLowerCase().replace(/[\s_-]+/g, '');
}

export function getSortedRouteStops(route) {
  return [...(route?.stops || [])].sort(
    (left, right) => (left.sequence || 0) - (right.sequence || 0)
  );
}

export function getRouteDisplayName(
  route,
  resolveStopName = (stop) => stop?.stopName || ''
) {
  const sortedStops = getSortedRouteStops(route);
  const firstStop = resolveStopName(sortedStops[0])?.trim();
  const lastStop = resolveStopName(
    sortedStops[sortedStops.length - 1]
  )?.trim();

  if (firstStop && lastStop) {
    return `${firstStop} - ${lastStop}`;
  }

  return route?.routeName || '';
}

export function formatDate(dateValue) {
  if (!dateValue) {
    return '--';
  }

  return new Date(dateValue).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}
